import { useContext, useEffect } from 'react';
import { QueryClient, QueryObserverOptions, useQuery } from 'react-query';
import { Inspection } from '@stormwater-software/lib-inspection-templates';
import AppContext from '../contexts/AppContext';
import projectInspectionApi, {
  ProjectInspection,
  SlimProjectInspection,
} from '../utils/api/projectInspection';
import projectSiteMapInspectionApi from '../utils/api/projectSiteMapInspection';
import {
  getFindingCounts,
  InspectionFindingCount,
} from '../utils/findingCount';
import customPrintInspectionApi from '../utils/api/custommPrintInspection';
import db from '../db/db';

export type InspectionWithFindingCount = ProjectInspection & {
  findingCount: InspectionFindingCount;
  commentables?: any;
  inspectionNumber?: number;
};

function getQueryKey(projectId: number) {
  return ['project', 'inspections', projectId];
}

export function addFindingCount(
  inspection: ProjectInspection,
): InspectionWithFindingCount {
  return { ...inspection, findingCount: getFindingCounts(inspection) };
}

export function useProjectInspections(
  projectId: number,
  queryOptions: Omit<
    QueryObserverOptions<InspectionWithFindingCount[]>,
    'queryKey' | 'queryFn' | 'keepPreviousData'
  > = {},
  page?: number,
  pageSize?: number,
  isMap?: boolean,
  inspection_template_type_id?: number
) {
  const appContext = useContext(AppContext);
  const query = useQuery<InspectionWithFindingCount[]>({
    queryKey: getQueryKey(projectId),
    queryFn: async () => {
      const isOnline = navigator.onLine;

      if (isOnline) {
        return appContext.loadData(async () => {

          if (projectId === 0) {
            return [];
          }
          if (isMap) {
            const data: any = projectId ? await projectInspectionApi.index(projectId, page, pageSize, inspection_template_type_id) : []

            return Array.isArray(data) ? data : (data.pageData || []);
          }

          const data: any = projectId
            ? await projectInspectionApi.index(projectId, page, pageSize, inspection_template_type_id)
            : [];

          return data.pageData.map(addFindingCount);
        })
      }
      else {

        if (!projectId || projectId === 0) {
          return [];
        }


        const data = await db.inspections.where('project_id').equals(Number(projectId)).sortBy('inspectionNumber')
          .then((inspections: any) => inspections.reverse())
          .catch((error: any) => {
            console.log('Error getting offline inspections', error);

            return [];
          }
          );


        if (!data || data.length === 0) {
          console.log("No offline inspection data available");

          return [];
        }
        const result = data.map((item: any) => {

          return addFindingCount(item);

        });


        return result;
      }
    },
    enabled: queryOptions.enabled !== undefined ? queryOptions.enabled : false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });

  useEffect(() => {
    if (queryOptions.enabled !== false) {
      query.refetch();
    }
  }, [projectId, inspection_template_type_id, queryOptions.enabled]);

  return query;
}

function getSlimQueryKey(projectId: number, inspection_template_type_id?: number) {
  return ['project', 'inspections', 'slim', projectId, inspection_template_type_id];
}

// Slim variant of useProjectInspections — drops `findings` and other heavy
// joins so the Map Editor list call is small. Findings are fetched per
// selected inspection on demand. Do not paginate: MapEditor's neighbour-id
// math walks the full ordered list.
export function useProjectInspectionsSlim(
  projectId: number,
  queryOptions: Omit<
    QueryObserverOptions<SlimProjectInspection[]>,
    'queryKey' | 'queryFn' | 'keepPreviousData'
  > = {},
  inspection_template_type_id?: number,
) {
  const appContext = useContext(AppContext);
  const query = useQuery<SlimProjectInspection[]>({
    queryKey: getSlimQueryKey(projectId, inspection_template_type_id),
    queryFn: async () => {
      const isOnline = navigator.onLine;

      if (isOnline) {
        return appContext.loadData(async () => {
          if (!projectId || projectId === 0) {
            return [];
          }
          const data: any = await projectInspectionApi.indexSlim(
            projectId,
            inspection_template_type_id,
          );

          return Array.isArray(data) ? data : (data.pageData || []);
        });
      } else {
        if (!projectId || projectId === 0) {
          return [];
        }

        const data = await db.inspections
          .where('project_id')
          .equals(Number(projectId))
          .sortBy('inspectionNumber')
          .then((inspections: any) => inspections.reverse())
          .catch((error: any) => {
            console.log('Error getting offline inspections (slim)', error);

            return [];
          });

        return data || [];
      }
    },
    enabled: queryOptions.enabled !== undefined ? queryOptions.enabled : false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });

  useEffect(() => {
    if (queryOptions.enabled !== false) {
      query.refetch();
    }
  }, [projectId, inspection_template_type_id, queryOptions.enabled]);

  return query;
}

export function customPrintInspections(
  projectId: number,
  queryOptions: Omit<
    QueryObserverOptions<InspectionWithFindingCount[]>,
    'queryKey' | 'queryFn' | 'enabled' | 'keepPreviousData'
  > = {},
) {
  const appContext = useContext(AppContext);
  const query = useQuery<InspectionWithFindingCount[]>({
    queryFn: async () => {
      const isOnline = navigator.onLine;

      if (isOnline) {
        return appContext.loadData(async () => {
          if (projectId === 0) {
            return [];
          }

          const data: any = projectId
            ? await customPrintInspectionApi.index(projectId)
            : [];

          return data;
        });
      } else {
        if (!projectId || projectId === 0) {
          return [];
        }

        const data = await db.inspections.where('project_id').equals(Number(projectId)).toArray().catch((error: any) => {
          console.log('Error getting offline custom print inspections', error);

          return [];
        }
        );


        if (!data || data.length === 0) {
          console.log("No offline custom print inspection data available");

          return [];
        }

        return data;
      }
    },
    enabled: false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    ...queryOptions
  });

  useEffect(() => {
    query.refetch();
  }, [projectId]);

  return query;
}

export function useProjectSiteMapInspections(
  projectId: number,
  siteMapId: number,
  queryOptions: Omit<
    QueryObserverOptions<Inspection[]>,
    'queryKey' | 'queryFn' | 'enabled' | 'keepPreviousData'
  > = {},
) {
  const appContext = useContext(AppContext);
  const query = useQuery<Inspection[]>({
    queryKey: ['project', 'inspections', projectId, siteMapId],
    queryFn: () =>
      appContext.loadData(async () => {
        if (projectId === 0) {
          return [];
        }

        if (!projectId) {
          return [];
        }

        const data: Inspection[] = siteMapId
          ? await projectSiteMapInspectionApi.index(projectId, siteMapId)
          : await projectSiteMapInspectionApi.show(projectId);

        return data;
      }),
    enabled: false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });

  useEffect(() => {
    query.refetch();
  }, [projectId, siteMapId]);

  return query;
}
export function setProjectInspectionsQueryData({
  projectId,
  data,
  queryClient,
}: {
  projectId: number;
  data: ProjectInspection[];
  queryClient: QueryClient;
}): void {
  const key = getQueryKey(projectId);

  queryClient.setQueryData<InspectionWithFindingCount[]>(
    key,
    data.map(addFindingCount),
  );
  queryClient.invalidateQueries(key);
}